import { useState } from 'react';
import { useAuth }  from '../../context/AuthContext';
import { apiFetch } from '../../lib/api';

export default function SubscriptionBanner() {
  const { profile } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [loadingPortal, setLoadingPortal] = useState(false);

  if (!profile) return null;

  const status = profile.subscription_status;

  // Days remaining on trial
  let trialDaysLeft: number | null = null;
  if (status === 'trialing' && profile.trial_ends_at) {
    const diff = new Date(profile.trial_ends_at).getTime() - Date.now();
    trialDaysLeft = Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }

  // Urgency levels for trial
  const isCritical  = status === 'trialing' && trialDaysLeft !== null && trialDaysLeft <= 3;
  const isUrgent    = status === 'trialing' && trialDaysLeft !== null && trialDaysLeft <= 7 && !isCritical;
  const isPastDue   = status === 'past_due';

  const isCanceled = status === 'canceled';

  // Don't show for active subscriptions
  if (status === 'active') return null;

  // Don't show if dismissed — except critical/past_due/canceled can't be dismissed
  if (dismissed && !isCritical && !isPastDue && !isCanceled) return null;

  // Nothing to show
  if (!isPastDue && !isCanceled && status !== 'trialing') return null;

  async function openPortal() {
    setLoadingPortal(true);
    try {
      const json = await apiFetch<{ url?: string }>('/api/stripe/create-portal-session', {
        method: 'POST',
        body: JSON.stringify({ returnPath: '/dashboard' }),
      });
      if (json.url) window.location.href = json.url;
    } finally {
      setLoadingPortal(false);
    }
  }

  // ── Styling by urgency ────────────────────────────────────────────────────
  const bannerStyle = isPastDue
    ? 'bg-red-600 text-white'
    : isCanceled
    ? 'bg-gray-800 text-white'
    : isCritical
    ? 'bg-orange-500 text-white'
    : isUrgent
    ? 'bg-amber-400 text-navy'
    : 'bg-navy text-white'; // early trial, subtle navy

  const btnStyle = isPastDue
    ? 'bg-white text-red-600 hover:bg-red-50'
    : isCanceled
    ? 'bg-sage text-navy hover:bg-sage/90'
    : isCritical
    ? 'bg-white text-orange-600 hover:bg-orange-50'
    : isUrgent
    ? 'bg-navy text-white hover:bg-navy/90'
    : 'bg-sage text-navy hover:bg-sage/90';

  const message = isPastDue
    ? '⚠️  Payment failed, update your card to keep sending cards.'
    : isCanceled
    ? '💌  Your subscription has ended. Re-subscribe to resume card sending.'
    : trialDaysLeft === 0
    ? '🚨  Your free trial ends TODAY. No cards will mail, add a payment method now to keep your account active.'
    : trialDaysLeft === 1
    ? '🚨  1 day left on your trial. Cards won\'t mail until you add a payment method. Your account will be downgraded otherwise.'
    : isCritical
    ? `⏰  ${trialDaysLeft} days left on your trial. Cards won't mail, not even holiday cards, until a payment method is on file.`
    : isUrgent
    ? `🕐  ${trialDaysLeft} days left on your free trial. Add a payment method now, cards won't mail without one, including any upcoming holidays.`
    : `✨  Free trial active, ${trialDaysLeft} days remaining. Cards won't mail until you add a payment method. No charge until your trial ends.`;

  const canDismiss = !isCritical && !isPastDue && !isCanceled;

  return (
    <div className={`${bannerStyle} px-4 py-2.5 transition-all`}>
      <div className="max-w-5xl mx-auto flex items-center justify-between gap-3">
        <p className="font-body text-sm font-medium leading-snug">
          {message}
        </p>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={openPortal}
            disabled={loadingPortal}
            className={`font-body text-xs font-semibold px-4 py-1.5 rounded-full whitespace-nowrap transition-colors disabled:opacity-60 ${btnStyle}`}
          >
            {loadingPortal
              ? 'Loading…'
              : isPastDue
              ? 'Update card'
              : isCanceled
              ? 'Re-subscribe'
              : 'Add payment method'}
          </button>

          {canDismiss && (
            <button
              type="button"
              onClick={() => setDismissed(true)}
              className="opacity-60 hover:opacity-100 transition-opacity flex-shrink-0"
              aria-label="Dismiss"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
